import React, { useEffect, useRef, useState } from 'react';
import { useKitManagement } from '../_shared/hooks/useKitManagement';
import { 
  FaBoxOpen, 
  FaUser, 
  FaEnvelope, 
  FaSignInAlt, 
  FaSignOutAlt, 
  FaClipboardList, 
  FaTshirt, 
  FaCheckCircle, 
  FaClock, 
  FaSearch, 
  FaFilter, 
  FaTags, 
  FaSyncAlt, 
  FaDice, 
  FaEdit, 
  FaSave, 
  FaTimes, 
  FaUndo, 
  FaUserCheck, 
  FaIdCard, 
  FaHashtag, 
  FaMicrochip, 
  FaBox, 
  FaFileAlt, 
  FaDoorOpen, 
  FaArrowLeft, 
  FaArrowRight, 
  FaCog, 
  FaPaintBrush, 
  FaPalette, 
  FaMagic, 
  FaRuler, 
  FaLayerGroup, 
  FaBarcode, 
  FaQrcode, 
  FaUserCircle, 
  FaUsers, 
  FaChartBar, 
  FaDatabase, 
  FaCloudUploadAlt, 
  FaDownload, 
  FaPrint, 
  FaEye, 
  FaEyeSlash, 
  FaLock, 
  FaUnlock, 
  FaPlus, 
  FaMinus, 
  FaTrash, 
  FaPencilAlt, 
  FaCheck, 
  FaExclamationTriangle, 
  FaInfoCircle, 
  FaQuestionCircle,
  FaSpinner
} from 'react-icons/fa';
import './KitManagement.css';

// ==========================================
// SUBCOMPONENTE: CeldaEditable (estilo Word)
// FUERA del componente principal para evitar re-renders
// ==========================================
const CeldaEditable = ({ valor, tipo = 'varchar', opciones = null, onGuardar }) => {
  const [editando, setEditando] = useState(false);
  const [valorTemp, setValorTemp] = useState('');
  const [valorOriginal, setValorOriginal] = useState('');
  const ref = useRef(null);

  const esFecha = /date/i.test(tipo);
  const esNumero = /int|decimal|double|float|numeric/i.test(tipo);

  const paraInput = (v) => {
    if (v == null || v === '') return '';
    if (esFecha) {
      const d = new Date(v);
      return isNaN(d) ? '' : d.toISOString().split('T')[0];
    }
    return String(v);
  };

  const paraMostrar = (v) => {
    if (v == null || v === '') return null;
    if (esFecha) {
      const d = new Date(v);
      return isNaN(d) ? String(v) : d.toLocaleDateString();
    }
    return String(v);
  };

  const abrir = () => {
    const valorInicial = paraInput(valor);
    setValorTemp(valorInicial);
    setValorOriginal(valorInicial);
    setEditando(true);
  };

  useEffect(() => {
    if (editando && ref.current) {
      ref.current.focus();
      if (ref.current.select) ref.current.select();
    }
  }, [editando]);

  const confirmar = async () => {
    if (!editando) return;
    setEditando(false);
    const nuevo = valorTemp === '' ? null : valorTemp;
    const original = valor == null || valor === '' ? null : paraInput(valor);
    if (nuevo === original) return;
    await onGuardar(nuevo);
  };

  const cancelar = () => {
    setEditando(false);
    setValorTemp(valorOriginal);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      confirmar();
    }
    if (e.key === 'Escape') {
      cancelar();
    }
  };

  if (!editando) {
    const mostrado = paraMostrar(valor);
    return (
      <div className="celda-editable" onClick={abrir} title="Clic para editar">
        {mostrado !== null ? mostrado : <span className="celda-vacia">—</span>}
      </div>
    );
  }

  if (opciones && opciones.length > 0) {
    return (
      <select
        ref={ref}
        className="celda-select"
        value={valorTemp}
        onChange={(e) => setValorTemp(e.target.value)}
        onBlur={confirmar}
        onKeyDown={onKeyDown}
      >
        <option value="">— Seleccionar —</option>
        {opciones.map((op) => (
          <option key={op} value={op}>
            {op}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      ref={ref}
      className="celda-input"
      type={esFecha ? 'date' : esNumero ? 'number' : 'text'}
      value={valorTemp}
      onChange={(e) => setValorTemp(e.target.value)}
      onBlur={confirmar}
      onKeyDown={onKeyDown}
    />
  );
};

// ==========================================
// COMPONENTE PRINCIPAL
// ==========================================
const KitManagement = ({ slug }) => {
  const [selectedId, setSelectedId] = useState(null);

  const {
    loading,
    error,
    successMessage,
    sistemaActivado,
    esAdministrador,
    encargadoActual,
    rangos,
    editandoRangos,
    competitors,
    stats,
    filtro,
    searchTerm,
    columnasDinamicas,
    columnasTabla,
    guardarCampo,
    setEncargadoActual,
    setEditandoRangos,
    setFiltro,
    setSearchTerm,
    setError,
    setSuccessMessage,
    registrarEncargado,
    activarSistema,
    asignarDorsalesAutomaticos,
    asignarDorsalManual,
    actualizarRangos,
    entregarKit,
    revertirEntrega,
    sincronizarCategorias,
  } = useKitManagement(slug, selectedId !== null);

  // Login
  const [nombreEncargado, setNombreEncargado] = useState('');
  const [emailEncargado, setEmailEncargado] = useState('');
  const [mostrarLogin, setMostrarLogin] = useState(true);

  // Ficha unificada (derivada de la lista para reflejar cambios en tiempo real)
  const [dorsalValue, setDorsalValue] = useState('');
  const [chipValue, setChipValue] = useState('');
  const [recibidoPor, setRecibidoPor] = useState('');
  const compFicha = competitors.find((c) => c.id === selectedId) || null;

  // Helpers
  const nombreDe = (comp) =>
    (columnasDinamicas?.nombre && comp[columnasDinamicas.nombre]) ||
    comp.nombre ||
    comp.nombres ||
    'Sin nombre';

  const identificacionDe = (comp) =>
    (columnasDinamicas?.identificacion && comp[columnasDinamicas.identificacion]) ||
    comp.ci ||
    comp.cedula ||
    '-';

  // ==========================================
  // HANDLERS
  // ==========================================
  const handleRegistrarEncargado = async (e) => {
    e.preventDefault();
    if (!nombreEncargado.trim()) {
      setError('Por favor ingresa tu nombre');
      return;
    }
    const result = await registrarEncargado(
      nombreEncargado.trim(),
      emailEncargado.trim()
    );
    if (result) setMostrarLogin(false);
  };

  const handleActivar = async (e) => {
    e.preventDefault();
    await activarSistema(nombreEncargado.trim(), emailEncargado.trim());
  };

  const handleAbrirFicha = (comp) => {
    setSelectedId(comp.id);
    setDorsalValue(comp.dorsal ?? '');
    setChipValue(comp.numero_chip || '');
    setRecibidoPor(nombreDe(comp));
  };

  const handleCerrarFicha = () => {
    setSelectedId(null);
  };

  const handleAsignarDorsalFicha = async () => {
    if (!compFicha || !esAdministrador) return;
    const d = parseInt(dorsalValue);
    if (!d || d < 1) return;
    if (d === compFicha.dorsal) return;
    const res = await asignarDorsalManual(compFicha.id, d);
    if (!res) {
      setDorsalValue(compFicha.dorsal || '');
    }
  };

  const handleEntregarFicha = async () => {
    if (!compFicha) return;

    const d = parseInt(dorsalValue);
    if (d && d >= 1 && d !== compFicha.dorsal) {
      const resDorsal = await asignarDorsalManual(compFicha.id, d);
      if (!resDorsal) return;
    }

    if (!compFicha.dorsal && !dorsalValue) {
      setError('Asigna un dorsal antes de entregar el kit.');
      return;
    }

    if (!recibidoPor.trim()) {
      setError('Ingresa el nombre de la persona que recibe el kit.');
      return;
    }

    const res = await entregarKit(compFicha.id, {
      numero_chip: chipValue || null,
      recibido_por: recibidoPor.trim(),
    });
    if (res) setSelectedId(null);
  };

  const handleSalir = () => {
    if (window.confirm('¿Salir del sistema de kits?')) {
      setMostrarLogin(true);
      setEncargadoActual(null);
      setError(null);
      setSuccessMessage(null);
    }
  };

  // ==========================================
  // RENDER: LOGIN
  // ==========================================
  if (mostrarLogin) {
    return (
      <div className="kit-login-container">
        <div className="kit-login-card">
          <h2><FaBoxOpen /> Sistema de Entrega de Kits</h2>
          <p>Registra tu nombre para comenzar</p>
          <form onSubmit={handleRegistrarEncargado}>
            <input
              type="text"
              placeholder="Tu nombre"
              value={nombreEncargado}
              onChange={(e) => setNombreEncargado(e.target.value)}
              className="kit-login-input"
              required
              disabled={loading}
            />
            <input
              type="email"
              placeholder="Tu email (opcional)"
              value={emailEncargado}
              onChange={(e) => setEmailEncargado(e.target.value)}
              className="kit-login-input"
              disabled={loading}
            />
            <button type="submit" className="kit-login-btn" disabled={loading}>
              {loading ? <><FaSpinner className="spin" /> Ingresando...</> : <><FaSignInAlt /> Ingresar</>}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ==========================================
  // RENDER: ACTIVACIÓN
  // ==========================================
  if (!sistemaActivado) {
    return (
      <div className="kit-activacion-container">
        <div className="kit-activacion-card">
          <h2><FaBoxOpen /> Sistema de Entrega de Kits</h2>
          <p className="kit-activacion-desc">
            <FaExclamationTriangle /> El sistema no está activado. Solo el primer encargado puede activarlo.
            <br />
            Esto creará las columnas necesarias en la base de datos y es{' '}
            <strong>PERMANENTE</strong>.
          </p>
          <form onSubmit={handleActivar}>
            <div className="kit-activacion-info">
              <p>
                <FaUser /> Activando como: <strong>{nombreEncargado}</strong>
              </p>
            </div>
            <button type="submit" className="kit-activacion-btn" disabled={loading}>
              {loading ? <><FaSpinner className="spin" /> Activando...</> : <><FaCheck /> Activar Sistema de Kits</>}
            </button>
            <button
              type="button"
              className="kit-activacion-cancel"
              onClick={() => {
                setNombreEncargado('');
                setEmailEncargado('');
                setMostrarLogin(true);
              }}
            >
              <FaArrowLeft /> Volver
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ==========================================
  // RENDER: SISTEMA ACTIVADO
  // ==========================================
  const columnasFormulario = columnasTabla.filter((c) => !c.sistema);

  return (
    <div className="kit-management-container">
      {successMessage && <div className="kit-success-message"><FaCheckCircle /> {successMessage}</div>}
      {error && <div className="kit-error-message"><FaExclamationTriangle /> {error}</div>}

      {/* Header */}
      <div className="kit-header">
        <div className="kit-user-info">
          <span className="kit-user-name"><FaUser /> {encargadoActual?.nombre}</span>
          <span className={`kit-user-role ${esAdministrador ? 'admin' : 'editor'}`}>
            {esAdministrador ? <FaCog /> : <FaUserCheck />} {esAdministrador ? 'Administrador' : 'Editor'}
          </span>
        </div>
        <div className="kit-stats">
          <span className="kit-stat-total"><FaDatabase /> Total: {stats.total}</span>
          <span className="kit-stat-entregados"><FaCheckCircle /> Entregados: {stats.entregados}</span>
          <span className="kit-stat-pendientes">
            <FaClock /> Pendientes: {stats.pendientes}
          </span>
        </div>
        <button className="kit-logout-btn" onClick={handleSalir}>
          <FaSignOutAlt /> Salir
        </button>
      </div>

      {/* Sección Admin */}
      {esAdministrador && (
        <div className="kit-admin-section">
          <div className="kit-admin-tools">
            <button
              onClick={asignarDorsalesAutomaticos}
              className="btn-primary"
              disabled={loading}
            >
              <FaMagic /> Asignar Dorsales Automático
            </button>
            <button
              onClick={() => setEditandoRangos(!editandoRangos)}
              className="btn-warning"
            >
              <FaEdit /> {editandoRangos ? 'Cerrar' : 'Editar'} Rangos
            </button>
            <button
              onClick={sincronizarCategorias}
              className="btn-secondary"
              disabled={loading}
            >
              <FaSyncAlt /> Sincronizar Categorías
            </button>
          </div>
          {editandoRangos && (
            <RangosEditor
              rangos={rangos}
              onSave={actualizarRangos}
              onCancel={() => setEditandoRangos(false)}
              loading={loading}
            />
          )}
        </div>
      )}

      {/* Toolbar */}
      <div className="kit-toolbar">
        <div className="kit-search">
          <input
            type="text"
            placeholder="Buscar por nombre, dorsal o CI..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="kit-search-input"
          />
          <FaSearch className="kit-search-icon" />
        </div>
        <div className="kit-filters">
          <button
            className={`kit-filter-btn ${filtro === 'todos' ? 'active' : ''}`}
            onClick={() => setFiltro('todos')}
          >
            <FaClipboardList /> Todos
          </button>
          <button
            className={`kit-filter-btn ${filtro === 'pendientes' ? 'active' : ''}`}
            onClick={() => setFiltro('pendientes')}
          >
            <FaClock /> Pendientes
          </button>
          <button
            className={`kit-filter-btn ${filtro === 'entregados' ? 'active' : ''}`}
            onClick={() => setFiltro('entregados')}
          >
            <FaCheckCircle /> Entregados
          </button>
        </div>
      </div>

      <p className="kit-hint">
        <FaInfoCircle /> Haz clic sobre un corredor para abrir su ficha, editar datos, asignar dorsal/chip y entregar el kit.
      </p>

      {/* Tabla */}
      {loading && competitors.length === 0 ? (
        <div className="kit-loading"><FaSpinner className="spin" /> Cargando...</div>
      ) : (
        <div className="kit-table-container">
          <table className="kit-table">
            <thead>
              <tr>
                <th><FaHashtag /></th>
                <th><FaTags /></th>
                {columnasFormulario.map((col) => (
                  <th key={col.campo}>{col.label}</th>
                ))}
                <th><FaMicrochip /></th>
                <th><FaClipboardList /></th>
                <th><FaUserCheck /></th>
              </tr>
            </thead>
            <tbody>
              {competitors.length === 0 ? (
                <tr>
                  <td colSpan={columnasFormulario.length + 5} className="kit-empty">
                    <FaDatabase /> No hay corredores
                  </td>
                </tr>
              ) : (
                competitors.map((comp, index) => (
                  <tr
                    key={comp.id}
                    className={`kit-row ${comp.kit_entregado ? 'entregado' : 'pendiente'}`}
                    onClick={() => handleAbrirFicha(comp)}
                    title="Clic para abrir la ficha"
                  >
                    <td>{index + 1}</td>
                    <td className="kit-dorsal">
                      {comp.dorsal ? (
                        <span className="badge-success"><FaHashtag /> {comp.dorsal}</span>
                      ) : (
                        <span className="kit-no-dorsal"><FaExclamationTriangle /> Sin dorsal</span>
                      )}
                    </td>
                    {columnasFormulario.map((col) => (
                      <td key={col.campo}>{comp[col.campo] ?? '-'}</td>
                    ))}
                    <td>{comp.numero_chip || '-'}</td>
                    <td>
                      {comp.kit_entregado ? (
                        <span className="badge-success"><FaCheckCircle /> Entregado</span>
                      ) : (
                        <span className="badge-warning"><FaClock /> Pendiente</span>
                      )}
                      {comp.fecha_entrega && (
                        <small className="kit-fecha">
                          <FaClock /> {new Date(comp.fecha_entrega).toLocaleString()}
                        </small>
                      )}
                    </td>
                    <td>{comp.entregado_por || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ========================================== */}
      {/* MODAL: FICHA UNIFICADA                     */}
      {/* ========================================== */}
      {compFicha && (
        <div className="kit-modal-overlay" onClick={handleCerrarFicha}>
          <div
            className="kit-modal kit-modal-ficha"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="kit-ficha-header">
              <h3><FaClipboardList /> Ficha del Corredor</h3>
              <button className="kit-modal-cancel" onClick={handleCerrarFicha}>
                <FaTimes />
              </button>
            </div>

            {compFicha.kit_entregado ? (
              <div className="kit-ficha-status entregado">
                <FaCheckCircle /> Kit entregado por{' '}
                <strong>{compFicha.entregado_por || '—'}</strong>
                {compFicha.fecha_entrega &&
                  ` el ${new Date(compFicha.fecha_entrega).toLocaleString()}`}
                {compFicha.recibido_por &&
                  ` · Recibido por: ${compFicha.recibido_por}`}
              </div>
            ) : (
              <div className="kit-ficha-status pendiente">
                <FaClock /> Kit pendiente de entrega
              </div>
            )}

            <div className="kit-modal-body">
              <div className="kit-ficha-seccion">
                <h4>
                  <FaFileAlt /> Datos del corredor{' '}
                  <span className="kit-ficha-hint">
                    <FaEdit /> clic sobre un campo para editarlo
                  </span>
                </h4>
                <div className="kit-ficha-grid">
                  {columnasFormulario.map((col) => (
                    <div key={col.campo} className="kit-ficha-campo">
                      <label>{col.label}</label>
                      <CeldaEditable
                        valor={compFicha[col.campo]}
                        tipo={col.tipo}
                        opciones={col.opciones}
                        onGuardar={(v) =>
                          guardarCampo(compFicha.id, col.campo, v)
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="kit-ficha-seccion">
                <h4><FaTshirt /> Asignación</h4>
                <div className="kit-ficha-grid dos">
                  <div className="kit-ficha-campo">
                    <label>
                      <FaHashtag /> N° Dorsal{' '}
                      {!esAdministrador && '(solo admin)'}
                    </label>
                    {esAdministrador ? (
                      <input
                        type="number"
                        min="1"
                        className="kit-modal-input"
                        value={dorsalValue}
                        onChange={(e) => setDorsalValue(e.target.value)}
                        onBlur={handleAsignarDorsalFicha}
                        onKeyDown={(e) =>
                          e.key === 'Enter' && handleAsignarDorsalFicha()
                        }
                        placeholder="Ej: 105"
                      />
                    ) : (
                      <div className="celda-editable">
                        {compFicha.dorsal
                          ? <><FaHashtag /> {compFicha.dorsal}</>
                          : <span className="celda-vacia">Sin dorsal</span>}
                      </div>
                    )}
                  </div>
                  <div className="kit-ficha-campo">
                    <label><FaMicrochip /> N° Chip</label>
                    <input
                      type="text"
                      className="kit-modal-input"
                      value={chipValue}
                      onChange={(e) => setChipValue(e.target.value)}
                      onBlur={() => {
                        const nuevo = chipValue || null;
                        if (compFicha.numero_chip !== nuevo) {
                          guardarCampo(compFicha.id, 'numero_chip', nuevo);
                        }
                      }}
                      placeholder="Ej: CH-99"
                    />
                  </div>
                </div>
              </div>

              {!compFicha.kit_entregado && (
                <div className="kit-ficha-seccion">
                  <h4><FaBox /> Entrega</h4>
                  <div className="kit-ficha-campo">
                    <label><FaUserCheck /> Entregado a (quién recibe)</label>
                    <input
                      type="text"
                      className="kit-modal-input"
                      value={recibidoPor}
                      onChange={(e) => setRecibidoPor(e.target.value)}
                      placeholder="Nombre de quien retira el kit"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="kit-modal-actions">
              {compFicha.kit_entregado && esAdministrador && (
                <button
                  className="btn-revertir"
                  onClick={() => revertirEntrega(compFicha.id)}
                >
                  <FaUndo /> Revertir entrega
                </button>
              )}
              <button className="kit-modal-cancel" onClick={handleCerrarFicha}>
                <FaTimes /> Cerrar
              </button>
              {!compFicha.kit_entregado && (
                <button
                  className="kit-modal-confirm"
                  onClick={handleEntregarFicha}
                  disabled={
                    loading || !(compFicha.dorsal || parseInt(dorsalValue) > 0)
                  }
                  title={
                    !compFicha.dorsal && !dorsalValue
                      ? 'Asigna un dorsal primero'
                      : ''
                  }
                >
                  {loading ? <><FaSpinner className="spin" /> Procesando...</> : <><FaBox /> Confirmar y Entregar</>}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// SUBCOMPONENTE: RangosEditor
// ==========================================
const RangosEditor = ({ rangos, onSave, onCancel, loading }) => {
  const [categorias, setCategorias] = useState(rangos.categorias || {});
  const [categoriaLibre, setCategoriaLibre] = useState(
    rangos.categoria_libre || { min: 1, max: 2000 }
  );

  useEffect(() => {
    setCategorias(rangos.categorias || {});
    setCategoriaLibre(rangos.categoria_libre || { min: 1, max: 2000 });
  }, [rangos]);

  const handleSave = () => {
    onSave({ categorias, categoria_libre: categoriaLibre });
  };

  return (
    <div className="kit-rangos-editor">
      <h4><FaTags /> Rangos de Dorsales por Categoría</h4>
      {Object.keys(categorias).length === 0 && (
        <p className="kit-rangos-vacio">
          <FaInfoCircle /> No hay categorías registradas. Usa <FaSyncAlt /> «Sincronizar Categorías» para detectarlas.
        </p>
      )}
      <div className="kit-rangos-grid">
        {Object.entries(categorias).map(([nombre, rango]) => (
          <div key={nombre} className="kit-rango-row">
            <span className="kit-rango-categoria"><FaTags /> {nombre}</span>
            <input
              type="number"
              value={rango.min ?? ''}
              onChange={(e) => {
                const newRangos = { ...categorias };
                newRangos[nombre] = {
                  ...rango,
                  min: e.target.value === '' ? null : parseInt(e.target.value) || 0,
                };
                setCategorias(newRangos);
              }}
              className="kit-rango-input"
              placeholder="Min"
            />
            <span>—</span>
            <input
              type="number"
              value={rango.max ?? ''}
              onChange={(e) => {
                const newRangos = { ...categorias };
                newRangos[nombre] = {
                  ...rango,
                  max: e.target.value === '' ? null : parseInt(e.target.value) || 0,
                };
                setCategorias(newRangos);
              }}
              className="kit-rango-input"
              placeholder="Max"
            />
          </div>
        ))}
        <div className="kit-rango-row kit-rango-libre">
          <span className="kit-rango-categoria"><FaUser /> Sin Categoría (Libre)</span>
          <input
            type="number"
            value={categoriaLibre.min ?? ''}
            onChange={(e) =>
              setCategoriaLibre({
                ...categoriaLibre,
                min: parseInt(e.target.value) || 0,
              })
            }
            className="kit-rango-input"
            placeholder="Min"
          />
          <span>—</span>
          <input
            type="number"
            value={categoriaLibre.max ?? ''}
            onChange={(e) =>
              setCategoriaLibre({
                ...categoriaLibre,
                max: parseInt(e.target.value) || 0,
              })
            }
            className="kit-rango-input"
            placeholder="Max"
          />
        </div>
      </div>
      <div className="kit-rangos-actions">
        <button onClick={onCancel} className="kit-rangos-cancel">
          <FaTimes /> Cancelar
        </button>
        <button
          onClick={handleSave}
          className="kit-rangos-save"
          disabled={loading}
        >
          {loading ? <><FaSpinner className="spin" /> Guardando...</> : <><FaSave /> Guardar Rangos</>}
        </button>
      </div>
    </div>
  );
};

export default KitManagement;